import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Star } from "lucide-react";

const QUALIFYING_CODES = new Set(["OPENING_0430_1300", "CLOSING_1800_0230"]);
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STATUS_DOT = { ELIGIBLE: "bg-emerald-500", WARNING: "bg-amber-500", BLOCKED: "bg-red-500" };

function toDate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function fmt(date) {
  return date.toISOString().slice(0, 10);
}
function addDays(dateStr, n) {
  const d = toDate(dateStr);
  d.setDate(d.getDate() + n);
  return fmt(d);
}
function dateRange(start, end) {
  const out = [];
  let cur = start;
  while (cur <= end) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

/** Center pane of the 3-pane Schedule Builder. Rows = employees (checked in the left pane),
 * columns = dates (a 7-day page within the schedule period, with Prev/Next/Today paging so a
 * long period never renders as one uncontrolled wide grid). Both the employee column and the
 * date header stay sticky while scrolling the other axis. */
export const ScheduleGrid = ({
  periodStart, periodEnd, assignments, board, checkedIds, highlightedEmployeeId,
  templates, isEditable, onEmptyCellClick, onShiftClick, onDropAssign, onDropMove,
}) => {
  const [pageStart, setPageStart] = useState(periodStart);
  const todayStr = fmt(new Date());

  const visibleDates = useMemo(() => {
    const pageEnd = addDays(pageStart, 6) > periodEnd ? periodEnd : addDays(pageStart, 6);
    return dateRange(pageStart, pageEnd);
  }, [pageStart, periodEnd]);

  const canPrev = pageStart > periodStart;
  const canNext = addDays(pageStart, 6) < periodEnd;

  const templateByCode = useMemo(() => new Map((templates || []).map((t) => [t.id, t])), [templates]);
  const rows = useMemo(() => (board?.employees || []).filter((e) => checkedIds.has(e.employee_id)), [board, checkedIds]);

  const assignmentsFor = (employeeId, date) =>
    (assignments || []).filter((a) => a.employee_id === employeeId && a.date === date);

  const isQualifying = (a) => {
    const tpl = templateByCode.get(a.shift_template_id);
    return tpl && QUALIFYING_CODES.has(tpl.code);
  };

  const handleCellDrop = (ev, employeeId, date) => {
    ev.preventDefault();
    const empId = ev.dataTransfer.getData("text/employee-id");
    const assignmentId = ev.dataTransfer.getData("text/assignment-id");
    if (assignmentId) onDropMove(assignmentId, employeeId, date);
    else if (empId) onDropAssign(empId, date);
  };

  return (
    <div className="flex flex-col h-full" data-testid="schedule-grid-pane">
      <div className="flex items-center justify-between p-2.5 border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
          <CalendarDays size={15} className="text-gray-500" />
          {visibleDates[0]} to {visibleDates[visibleDates.length - 1]}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="grid-prev-week" disabled={!canPrev} onClick={() => setPageStart(addDays(pageStart, -7))} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronLeft size={14} /></button>
          <button
            data-testid="grid-today"
            onClick={() => { const clamped = todayStr < periodStart ? periodStart : todayStr > periodEnd ? periodEnd : todayStr; setPageStart(clamped); }}
            className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold hover:bg-gray-50"
          >
            Today
          </button>
          <button data-testid="grid-next-week" disabled={!canNext} onClick={() => setPageStart(addDays(pageStart, 7))} className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50"><ChevronRight size={14} /></button>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {rows.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-gray-500 p-8 text-center" data-testid="schedule-grid-empty">
            Check employees in the left panel to add them to this grid.
          </div>
        ) : (
          <table className="border-collapse w-full text-sm" data-testid="schedule-grid-table">
            <thead>
              <tr>
                <th className="sticky top-0 left-0 z-30 bg-gray-50 border-b border-r border-gray-200 px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase w-44 min-w-[176px]">
                  Employee
                </th>
                {visibleDates.map((date) => (
                  <th
                    key={date}
                    data-testid={`grid-date-header-${date}`}
                    className={`sticky top-0 z-20 border-b border-gray-200 px-2 py-2 text-center text-xs font-bold uppercase min-w-[132px] ${date === todayStr ? "bg-blue-50 text-blue-700" : "bg-gray-50 text-gray-500"}`}
                  >
                    {WEEKDAY_LABELS[toDate(date).getDay() === 0 ? 6 : toDate(date).getDay() - 1]} <span className="font-normal">{date.slice(5)}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((emp) => (
                <tr key={emp.employee_id} className={highlightedEmployeeId === emp.employee_id ? "bg-blue-50/60" : ""} data-testid={`grid-row-${emp.employee_id}`}>
                  <td className="sticky left-0 z-10 bg-white border-b border-r border-gray-100 px-3 py-2 align-top">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[emp.eligibility_status]}`} />
                      <p className="text-xs font-semibold text-gray-800 truncate">{emp.legal_name}</p>
                    </div>
                    <p className="text-[10px] text-gray-500 truncate pl-3.5">{emp.position || "-"}</p>
                  </td>
                  {visibleDates.map((date) => {
                    const dayAssignments = assignmentsFor(emp.employee_id, date);
                    return (
                      <td
                        key={date}
                        data-testid={`grid-cell-${emp.employee_id}-${date}`}
                        onDragOver={(e) => isEditable && e.preventDefault()}
                        onDrop={(e) => isEditable && handleCellDrop(e, emp.employee_id, date)}
                        className={`border-b border-r border-gray-100 p-1 align-top ${date === todayStr ? "bg-blue-50/30" : ""}`}
                      >
                        <div className="space-y-1">
                          {dayAssignments.map((a) => (
                            <div
                              key={a.id}
                              draggable={isEditable}
                              onDragStart={(e) => e.dataTransfer.setData("text/assignment-id", a.id)}
                              onClick={() => onShiftClick(a)}
                              data-testid={`grid-chip-${a.id}`}
                              className={`rounded-md border px-1.5 py-1 text-[11px] cursor-pointer hover:shadow-sm transition-shadow ${
                                a.double_shift_link_id ? "bg-purple-50 border-purple-200 text-purple-800" : "bg-blue-50 border-blue-200 text-blue-800"
                              }`}
                              title="Click to view/edit this shift"
                            >
                              <div className="flex items-center gap-1 font-semibold">
                                {isQualifying(a) && <Star size={10} className="fill-amber-400 text-amber-500 shrink-0" />}
                                <span className="truncate">{a.start_time}-{a.end_time}{a.crosses_midnight && "+1"}</span>
                              </div>
                              {a.role && <p className="truncate text-[10px] opacity-70">{a.role}</p>}
                            </div>
                          ))}
                          {isEditable && (
                            <button
                              data-testid={`grid-add-${emp.employee_id}-${date}`}
                              onClick={() => onEmptyCellClick(emp.employee_id, date)}
                              className="w-full flex items-center justify-center gap-1 py-1 rounded-md border border-dashed border-gray-200 text-gray-300 hover:text-blue-500 hover:border-blue-300 transition-colors"
                              title="Assign a shift"
                            >
                              <Plus size={11} />
                            </button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
